import type {
	AflossingsVormType,
	HypotheekEntiteitType,
	InformatieBericht,
	LeningdeelEntiteitType,
	LeningEntiteitType,
} from "@loanlink-nl/model-hdn/InformatieBericht";
import type {
	ExistingMortgage,
	ExistingMortgagePart,
	RepaymentForm,
} from "@loanlink-nl/model-app";
import { splitHdnValue } from "./hdnHelpers";

type MapInformatieBerichtOptions = {
	existingMortgage: ExistingMortgage;
	informatieBericht: InformatieBericht;
	mortgageNumber?: string;
	expectedLenderCode?: string;
};

type SelectHypotheekResult =
	| {
			status: "selected";
			hypotheek: HypotheekEntiteitType;
	  }
	| {
			status: "not-found" | "ambiguous";
	  };

export type MapInformatieBerichtToExistingMortgageResult =
	| {
			status: "mapped";
			mortgage: ExistingMortgage;
	  }
	| {
			status: "not-mapped";
			reason:
				| "missing-informatie"
				| "hypotheek-not-found"
				| "hypotheek-ambiguous"
				| "missing-loans";
	  };

const repaymentFormByAflossingsVorm: Partial<
	Record<AflossingsVormType, RepaymentForm>
> = {
	"01 Annuiteit": "annuity",
	"02 Lineair": "linear",
	"03 Levensverzekering": "life-insurance",
	"05 Aflossingsvrij": "interest-only",
	"10 Beleggingshypotheek": "investment-mortgage",
	"13 Spaarrekening": "savings-mortgage",
};

function parseHdnNumber(value?: string): number | undefined {
	if (!value) {
		return undefined;
	}

	const normalized = value.replace(",", ".");
	const parsed = Number.parseFloat(normalized);
	if (!Number.isFinite(parsed)) {
		return undefined;
	}

	return parsed;
}

function parseHdnBoolean(value?: string): boolean | undefined {
	if (!value) {
		return undefined;
	}

	if (value.startsWith("J")) {
		return true;
	}

	if (value.startsWith("N")) {
		return false;
	}

	return undefined;
}

function sumDefined(values: Array<number | undefined>): number | undefined {
	let sum = 0;
	let hasValue = false;
	for (const value of values) {
		if (Number.isFinite(value)) {
			sum += value as number;
			hasValue = true;
		}
	}

	return hasValue ? sum : undefined;
}

function lenderCodesMatch(expected?: string, actual?: string): boolean {
	if (!expected || !actual) {
		return true;
	}

	const normalizedExpected = expected.trim().toLowerCase();
	const normalizedActual = actual.trim().toLowerCase();
	if (normalizedExpected === normalizedActual) {
		return true;
	}

	const [, expectedLabel] = splitHdnValue(expected);
	const [, actualLabel] = splitHdnValue(actual);
	const normalizedExpectedLabel = expectedLabel.trim().toLowerCase();
	const normalizedActualLabel = actualLabel.trim().toLowerCase();

	if (!normalizedExpectedLabel || !normalizedActualLabel) {
		return false;
	}

	return (
		normalizedExpectedLabel === normalizedActualLabel ||
		normalizedExpectedLabel.includes(normalizedActualLabel) ||
		normalizedActualLabel.includes(normalizedExpectedLabel)
	);
}

function selectHypotheek(
	hypotheken: HypotheekEntiteitType[] | undefined,
	mortgageNumber?: string,
	expectedLenderCode?: string,
): SelectHypotheekResult {
	if (!hypotheken || hypotheken.length === 0) {
		return { status: "not-found" };
	}

	if (mortgageNumber) {
		const hypotheek = hypotheken.find(
			(hypotheek) => hypotheek.BestaandeHypotheekNr === mortgageNumber,
		);

		if (hypotheek) {
			return { status: "selected", hypotheek };
		}

		if (hypotheken.length === 1) {
			const onlyHypotheek = hypotheken[0]!;
			const lenderCode = (
				Array.isArray(onlyHypotheek.Lening)
					? onlyHypotheek.Lening?.[0]
					: onlyHypotheek.Lening
			)?.Financier;

			if (lenderCodesMatch(expectedLenderCode, lenderCode)) {
				return { status: "selected", hypotheek: onlyHypotheek };
			}
		}

		return { status: "not-found" };
	}

	return hypotheken.length === 1
		? { status: "selected", hypotheek: hypotheken[0]! }
		: { status: "ambiguous" };
}

function getAllLeningdelen(
	hypotheek: HypotheekEntiteitType,
): LeningdeelEntiteitType[] {
	return (
		(Array.isArray(hypotheek.Lening) ? hypotheek.Lening[0] : hypotheek.Lening)
			?.Leningdeel ?? []
	);
}

function mapMortgageParts(
	parts: LeningdeelEntiteitType[],
	existingMortgage: ExistingMortgage,
): ExistingMortgagePart[] {
	const existingByExternalId = new Map(
		(existingMortgage.mortgageParts ?? [])
			.filter((part) => part.lenderMortgagePartId)
			.map((part) => [part.lenderMortgagePartId as string, part]),
	);

	return parts.map((part) => {
		const externalId = part.NrBestaandLeningdeel || part.Volgnummer?.toString();
		const existingPart = externalId
			? existingByExternalId.get(externalId)
			: undefined;

		// RentePct is a percentage, interestRate is a decimal fraction
		const interestRateRaw = parseHdnNumber(part.RentePct);
		const interestRate =
			interestRateRaw !== undefined ? interestRateRaw / 100 : undefined;

		return {
			id: existingPart?.id ?? crypto.randomUUID(),
			lenderMortgagePartId: externalId,
			startDate: part.IngangsDt,
			endDate: part.EindDt,
			interestRate,
			fixedInterestEndDate: part.RentevastePeriodeEindDt,
			amountOriginal: parseHdnNumber(part.LeningDeelBedrag),
			amountCurrent: parseHdnNumber(part.RestantSaldoHuidigLeningdeel),
			repaymentForm: repaymentFormByAflossingsVorm[part.AflossingsVorm],
		};
	});
}

function mapNhg(parts: LeningdeelEntiteitType[]): boolean | undefined {
	const values = parts
		.map((part) => parseHdnBoolean(part.NHGJN))
		.filter((value) => value !== undefined);

	if (values.length === 0) {
		return undefined;
	}

	if (values.every((value) => value === false)) {
		return false;
	}

	return true;
}

export function mapInformatieBerichtToExistingMortgage({
	existingMortgage,
	informatieBericht,
	mortgageNumber,
	expectedLenderCode,
}: MapInformatieBerichtOptions): MapInformatieBerichtToExistingMortgageResult {
	const informatie = informatieBericht?.InformatieBericht;
	if (!informatie) {
		return { status: "not-mapped", reason: "missing-informatie" };
	}

	const hypotheekSelection = selectHypotheek(
		informatie.Hypotheek,
		mortgageNumber,
		expectedLenderCode ?? existingMortgage.lenderCode,
	);
	if (hypotheekSelection.status !== "selected") {
		return {
			status: "not-mapped",
			reason:
				hypotheekSelection.status === "ambiguous"
					? "hypotheek-ambiguous"
					: "hypotheek-not-found",
		};
	}

	const { hypotheek } = hypotheekSelection;

	const firstLening: LeningEntiteitType = (
		Array.isArray(hypotheek.Lening) ? hypotheek.Lening[0] : hypotheek.Lening
	) as LeningEntiteitType;
	const allLeningdelen = getAllLeningdelen(hypotheek);
	const mappedParts = mapMortgageParts(allLeningdelen, existingMortgage);

	const amountOriginal =
		sumDefined(mappedParts.map((part) => part.amountOriginal)) ??
		sumDefined([parseHdnNumber(firstLening.HypotheekBedrag)]);
	const amountCurrent = sumDefined(
		mappedParts.map((part) => part.amountCurrent),
	);

	return {
		status: "mapped",
		mortgage: {
			...existingMortgage,
			isManual: false,
			lenderMortgageId:
				hypotheek.BestaandeHypotheekNr ?? existingMortgage.lenderMortgageId,
			lenderCode: firstLening.Financier ?? existingMortgage.lenderCode,
			productCode: firstLening.CodeLeningMij ?? existingMortgage.productCode,
			amountRegistration:
				parseHdnNumber(hypotheek.HypothecaireInschrijving) ??
				existingMortgage.amountRegistration,
			amountOriginal: amountOriginal ?? existingMortgage.amountOriginal,
			amountCurrent: amountCurrent ?? existingMortgage.amountCurrent,
			nhg: mapNhg(allLeningdelen) ?? existingMortgage.nhg,
			mortgageParts:
				mappedParts.length > 0 ? mappedParts : existingMortgage.mortgageParts,
		},
	};
}
